import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonCheckbox,
  IonContent,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonProgressBar,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonText,
  IonTextarea,
  IonToast,
} from "@ionic/react";
import {
  trashOutline,
  cameraOutline,
  imageOutline,
  arrowBackOutline,
  arrowForwardOutline,
  saveOutline,
} from "ionicons/icons";

import Header from "src/components/Header";
import {
  obtenerCatalogoRelevamientoOperativo,
  buscarPuntosServicioAsignados,
  obtenerContextoPS,
  crearRelevamientoOperativo,
  actualizarRelevamientoOperativo,
  obtenerRelevamientoOperativo,
  RelevamientoOperativoCatalogo,
  PuntoServicioAsignado,
  FiscalCatalogo,
  FotoPayload,
  MultiOpcion,
  RelevamientoOperativoPayload,
} from "src/services/relevamientoOperativo";

interface Toast {
  show: boolean;
  msg: string;
  color?: string;
}

const hoy = () => new Date().toISOString().slice(0, 10);

const codigosDeMulti = (lista: MultiOpcion[] | undefined): string[] =>
  (lista || []).map((m) => m.codigo);

type Opcion = { codigo: string; etiqueta: string };

const RelevamientoOperativo: React.FC = () => {
  const history = useHistory();
  const params = useParams<{ id?: string }>();
  const editandoId = params.id ? Number(params.id) : null;
  const esEdicion = !!editandoId;

  const [cargandoInicial, setCargandoInicial] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [catalogo, setCatalogo] = useState<RelevamientoOperativoCatalogo | null>(null);
  const [toast, setToast] = useState<Toast>({ show: false, msg: "" });

  // Wizard
  const [paso, setPaso] = useState(1);

  // Sección 1
  const [psBusqueda, setPsBusqueda] = useState("");
  const [psResultados, setPsResultados] = useState<PuntoServicioAsignado[]>([]);
  const [psBuscando, setPsBuscando] = useState(false);
  const [psSeleccionado, setPsSeleccionado] = useState<PuntoServicioAsignado | null>(null);
  const [fiscalSeleccionado, setFiscalSeleccionado] = useState<FiscalCatalogo | null>(null);
  const [fechaRelevamiento, setFechaRelevamiento] = useState<string>(hoy());
  const [segmentoOperativo, setSegmentoOperativo] = useState("");
  const [cantidadPersonas, setCantidadPersonas] = useState("");
  const [cantidadPersonasOtro, setCantidadPersonasOtro] = useState("");

  // Sección 2
  const [tieneEspacio, setTieneEspacio] = useState("");
  const [pesa, setPesa] = useState("");
  const [pesaOtro, setPesaOtro] = useState("");
  const [reporte, setReporte] = useState("");
  const [reporteOtro, setReporteOtro] = useState("");
  const [tiposResiduos, setTiposResiduos] = useState<string[]>([]);
  const [reciclableMas, setReciclableMas] = useState("");
  const [contenedores, setContenedores] = useState("");

  // Sección 3A
  const [bolsasGenerales, setBolsasGenerales] = useState("");
  const [bolsasGeneralesOtro, setBolsasGeneralesOtro] = useState("");
  const [diasGenerales, setDiasGenerales] = useState("");
  const [quienRetiraGeneral, setQuienRetiraGeneral] = useState<string[]>([]);

  // Sección 3B
  const [bolsasOrganicas, setBolsasOrganicas] = useState("");
  const [bolsasOrganicasOtro, setBolsasOrganicasOtro] = useState("");
  const [diasOrganicos, setDiasOrganicos] = useState("");
  const [bolsasReciclables, setBolsasReciclables] = useState("");
  const [bolsasReciclablesOtro, setBolsasReciclablesOtro] = useState("");
  const [reciclablesMezclados, setReciclablesMezclados] = useState("");
  const [diasReciclables, setDiasReciclables] = useState("");
  const [retiraReciclablesInfo, setRetiraReciclablesInfo] = useState("");
  const [frecuenciaReciclables, setFrecuenciaReciclables] = useState("");
  const [quienRetira3b, setQuienRetira3b] = useState<string[]>([]);

  // Sección 4 - Observaciones Finales
  const [materialNoCubierto, setMaterialNoCubierto] = useState("");
  const [contextoCliente, setContextoCliente] = useState("");
  const [fotos, setFotos] = useState<FotoPayload[]>([]);
  const [motivoEdicion, setMotivoEdicion] = useState("");

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const seccion3 = useMemo<"A" | "B" | "">(() => {
    if (!contenedores) return "";
    return contenedores === "NO_TODO_JUNTO" ? "A" : "B";
  }, [contenedores]);

  const totalPasos = 4;

  // Carga inicial
  useEffect(() => {
    let activo = true;
    const cargar = async () => {
      setCargandoInicial(true);
      try {
        const cat = await obtenerCatalogoRelevamientoOperativo();
        if (!activo) return;
        setCatalogo(cat);

        if (editandoId) {
          const detalle = await obtenerRelevamientoOperativo(editandoId);
          if (!activo) return;
          setPsSeleccionado({
            id: detalle.ps_id,
            ps_nombre: detalle.ps_nombre || "",
            ps_codigo: detalle.ps_codigo || "",
          });
          setPsBusqueda(detalle.ps_nombre || "");
          if (detalle.fiscal_asignado_id) {
            setFiscalSeleccionado({
              fiscal_id: detalle.fiscal_asignado_id,
              fiscal_cedula: detalle.fiscal_asignado_cedula || "",
              fiscal_nombre: detalle.fiscal_asignado_nombre || "",
              cargo: "",
            });
          }
          setFechaRelevamiento(detalle.fecha_relevamiento || hoy());
          setSegmentoOperativo(detalle.segmento_operativo || "");
          setCantidadPersonas(detalle.cantidad_personas);
          setCantidadPersonasOtro(detalle.cantidad_personas_otro || "");
          setTieneEspacio(detalle.tiene_espacio_residuos);
          setPesa(detalle.pesa_residuos);
          setPesaOtro(detalle.pesa_residuos_otro || "");
          setReporte(detalle.reporte_residuos);
          setReporteOtro(detalle.reporte_residuos_otro || "");
          setTiposResiduos(codigosDeMulti(detalle.multi?.TIPOS_RESIDUOS));
          setReciclableMas(detalle.reciclable_mas_generado);
          setContenedores(detalle.contenedores_diferenciados);
          setBolsasGenerales(detalle.bolsas_generales || "");
          setBolsasGeneralesOtro(detalle.bolsas_generales_otro || "");
          setDiasGenerales(detalle.dias_retiran_generales || "");
          setQuienRetiraGeneral(codigosDeMulti(detalle.multi?.QUIEN_RETIRA_GENERAL));
          setBolsasOrganicas(detalle.bolsas_organicas || "");
          setBolsasOrganicasOtro(detalle.bolsas_organicas_otro || "");
          setDiasOrganicos(detalle.dias_retiran_organicos || "");
          setBolsasReciclables(detalle.bolsas_reciclables || "");
          setBolsasReciclablesOtro(detalle.bolsas_reciclables_otro || "");
          setReciclablesMezclados(detalle.reciclables_mezclados || "");
          setDiasReciclables(detalle.dias_reciclables || "");
          setRetiraReciclablesInfo(detalle.retira_reciclables_info || "");
          setFrecuenciaReciclables(detalle.frecuencia_retiran_reciclables || "");
          setQuienRetira3b(codigosDeMulti(detalle.multi?.QUIEN_RETIRA_3B));
          setMaterialNoCubierto(detalle.material_no_cubierto || "");
          setContextoCliente(detalle.contexto_cliente || "");
          setFotos(
            (detalle.fotos || []).map((f) => ({
              public_id: f.public_id,
              url_secure: f.url_secure,
              formato: f.formato || undefined,
              ancho: f.ancho ?? undefined,
              alto: f.alto ?? undefined,
              bytes: f.bytes ?? undefined,
              nombre_archivo: f.nombre_archivo || undefined,
              orden: f.orden,
            }))
          );
        }
      } catch (error: any) {
        if (!activo) return;
        setToast({
          show: true,
          msg: error?.message || "No se pudo cargar el formulario.",
          color: "danger",
        });
      } finally {
        if (activo) setCargandoInicial(false);
      }
    };
    cargar();
    return () => {
      activo = false;
    };
  }, [editandoId]);

  // Buscador de PS
  useEffect(() => {
    if (esEdicion) return;
    let activo = true;
    const termino = psBusqueda.trim();
    if (psSeleccionado && termino !== psSeleccionado.ps_nombre) {
      setPsSeleccionado(null);
      setFiscalSeleccionado(null);
      setSegmentoOperativo("");
    }
    if (termino.length < 1) {
      setPsResultados([]);
      return;
    }
    const t = window.setTimeout(async () => {
      setPsBuscando(true);
      try {
        const datos = await buscarPuntosServicioAsignados(termino);
        if (!activo) return;
        setPsResultados((datos || []).slice(0, 10));
      } catch {
        if (!activo) return;
        setPsResultados([]);
      } finally {
        if (activo) setPsBuscando(false);
      }
    }, 250);
    return () => {
      activo = false;
      window.clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [psBusqueda]);

  const seleccionarPS = async (ps: PuntoServicioAsignado) => {
    setPsResultados([]);
    setPsSeleccionado(ps);
    setPsBusqueda(ps.ps_nombre);
    try {
      const ctx = await obtenerContextoPS(ps.id);
      const nombre = String(ctx?.fiscal?.nombre || "").trim();
      if (nombre) {
        setFiscalSeleccionado({
          fiscal_id: 0,
          fiscal_cedula: "",
          fiscal_nombre: nombre,
          cargo: "",
        });
      } else {
        setFiscalSeleccionado(null);
      }
      setSegmentoOperativo(String(ctx?.segmento_operativo || "").trim());
    } catch (error: any) {
      setToast({
        show: true,
        msg: error?.message || "No se pudo obtener el contexto del PS.",
        color: "warning",
      });
    }
  };

  const toggleMulti = (current: string[], codigo: string): string[] => {
    return current.includes(codigo)
      ? current.filter((c) => c !== codigo)
      : [...current, codigo];
  };

  const onSeleccionarFotos = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    const nuevas: FotoPayload[] = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      if (!f.type.startsWith("image/")) continue;
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(f);
      }).catch(() => "");
      if (dataUrl) {
        nuevas.push({
          data_url: dataUrl,
          nombre_archivo: f.name,
          mime_type: f.type,
        });
      }
    }
    if (nuevas.length) setFotos((prev) => [...prev, ...nuevas]);
    if (event.target) event.target.value = "";
  };

  const eliminarFoto = (idx: number) => {
    setFotos((prev) => prev.filter((_, i) => i !== idx));
  };

  // Validaciones por paso
  const validarPaso = (n: number): string | null => {
    if (n === 1) {
      if (!psSeleccionado) return "Debe seleccionar un punto de servicio.";
      // segmento_operativo se autocompleta desde EMSYS — no se valida
      if (!cantidadPersonas) return "Debe indicar la cantidad de personas.";
      if (cantidadPersonas === "OTRO" && !cantidadPersonasOtro.trim()) {
        return 'Debe completar el detalle de "Otro" en cantidad de personas.';
      }
    }
    if (n === 2) {
      if (!tieneEspacio) return "Debe responder si el cliente tiene espacio para residuos.";
      if (!pesa) return "Debe responder si el cliente pesa sus residuos.";
      if (!reporte) return "Debe responder si el cliente tiene reporte de residuos.";
      if (tiposResiduos.length === 0) return "Debe marcar al menos un tipo de residuo.";
      if (!reciclableMas) return "Debe indicar el reciclable más generado.";
      if (!contenedores) return "Debe responder si tiene contenedores diferenciados.";
    }
    // 3 y 4 sin obligatorios
    return null;
  };

  const irSiguiente = () => {
    const err = validarPaso(paso);
    if (err) {
      setToast({ show: true, msg: err, color: "warning" });
      return;
    }
    setPaso((p) => Math.min(totalPasos, p + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const irAnterior = () => {
    setPaso((p) => Math.max(1, p - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const construirPayload = (): RelevamientoOperativoPayload => {
    const base: RelevamientoOperativoPayload = {
      ps_id: psSeleccionado!.id,
      ps_codigo: psSeleccionado!.ps_codigo,
      ps_nombre: psSeleccionado!.ps_nombre,
      fiscal_asignado_id: fiscalSeleccionado?.fiscal_id ?? null,
      fiscal_asignado_cedula: fiscalSeleccionado?.fiscal_cedula ?? null,
      fiscal_asignado_nombre: fiscalSeleccionado?.fiscal_nombre ?? null,
      fecha_relevamiento: fechaRelevamiento,
      // segmento_operativo lo setea el backend desde EMSYS; se envía informativo
      segmento_operativo: segmentoOperativo || undefined,
      cantidad_personas: cantidadPersonas,
      cantidad_personas_otro: cantidadPersonas === "OTRO" ? cantidadPersonasOtro.trim() : undefined,
      tiene_espacio_residuos: tieneEspacio,
      pesa_residuos: pesa,
      pesa_residuos_otro: pesa === "OTRO" ? pesaOtro.trim() : undefined,
      reporte_residuos: reporte,
      reporte_residuos_otro: reporte === "OTRO" ? reporteOtro.trim() : undefined,
      tipos_residuos: tiposResiduos,
      reciclable_mas_generado: reciclableMas,
      contenedores_diferenciados: contenedores,
      material_no_cubierto: materialNoCubierto.trim() || undefined,
      contexto_cliente: contextoCliente.trim() || undefined,
      fotos: fotos.map((f, idx) => ({ ...f, orden: idx })),
    };
    if (seccion3 === "A") {
      base.bolsas_generales = bolsasGenerales || undefined;
      base.bolsas_generales_otro =
        bolsasGenerales === "OTRO" ? bolsasGeneralesOtro.trim() || undefined : undefined;
      base.dias_retiran_generales = diasGenerales || undefined;
      base.quien_retira_general = quienRetiraGeneral;
    } else if (seccion3 === "B") {
      base.bolsas_organicas = bolsasOrganicas || undefined;
      base.bolsas_organicas_otro =
        bolsasOrganicas === "OTRO" ? bolsasOrganicasOtro.trim() || undefined : undefined;
      base.dias_retiran_organicos = diasOrganicos || undefined;
      base.bolsas_reciclables = bolsasReciclables || undefined;
      base.bolsas_reciclables_otro =
        bolsasReciclables === "OTRO" ? bolsasReciclablesOtro.trim() || undefined : undefined;
      base.reciclables_mezclados = reciclablesMezclados || undefined;
      base.dias_reciclables = diasReciclables || undefined;
      base.retira_reciclables_info = retiraReciclablesInfo.trim() || undefined;
      base.frecuencia_retiran_reciclables = frecuenciaReciclables || undefined;
      base.quien_retira_3b = quienRetira3b;
    }
    if (esEdicion) {
      base.motivo_edicion = motivoEdicion.trim() || undefined;
    }
    return base;
  };

  const guardar = async () => {
    // Validar todos los pasos previos antes de guardar
    for (let n = 1; n <= totalPasos; n++) {
      const err = validarPaso(n);
      if (err) {
        setPaso(n);
        setToast({ show: true, msg: err, color: "warning" });
        return;
      }
    }
    setGuardando(true);
    try {
      const payload = construirPayload();
      if (esEdicion && editandoId) {
        await actualizarRelevamientoOperativo(editandoId, payload);
        setToast({ show: true, msg: "Repro actualizado.", color: "success" });
      } else {
        await crearRelevamientoOperativo(payload);
        setToast({ show: true, msg: "Repro creado.", color: "success" });
      }
      setTimeout(() => history.replace("/relevamiento-operativo"), 700);
    } catch (error: any) {
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.info ||
        error?.message ||
        "No se pudo guardar el Repro.";
      setToast({ show: true, msg, color: "danger" });
    } finally {
      setGuardando(false);
    }
  };

  if (cargandoInicial) {
    return (
      <IonPage>
        <Header titulo={esEdicion ? "Editar Repro" : "Repro"} />
        <IonContent id="relevamiento-operativo" className="ion-padding">
          <div style={{ display: "flex", justifyContent: "center", paddingTop: 32 }}>
            <IonSpinner name="crescent" />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (!catalogo) {
    return (
      <IonPage>
        <Header titulo="Repro" />
        <IonContent id="relevamiento-operativo" className="ion-padding">
          <IonText color="medium">No se pudo cargar el catálogo del formulario.</IonText>
        </IonContent>
      </IonPage>
    );
  }

  // Renderiza una pregunta de selección única como IonSelect (action-sheet)
  const renderSelect = (
    titulo: string,
    valor: string,
    onChange: (v: string) => void,
    opciones: Opcion[],
    placeholder = "Elegir opción"
  ) => (
    <IonItem>
      <IonLabel position="stacked" className="ion-text-wrap">
        {titulo}
      </IonLabel>
      <IonSelect
        value={valor}
        placeholder={placeholder}
        interface="action-sheet"
        cancelText="Cancelar"
        interfaceOptions={{ header: titulo }}
        onIonChange={(e) => onChange(String(e.detail.value || ""))}
      >
        {opciones.map((o) => (
          <IonSelectOption key={o.codigo} value={o.codigo}>
            {o.etiqueta}
          </IonSelectOption>
        ))}
      </IonSelect>
    </IonItem>
  );

  const renderCheckboxGroup = (
    titulo: string,
    seleccionados: string[],
    setSeleccionados: (v: string[]) => void,
    opciones: Opcion[]
  ) => (
    <>
      <IonItem lines="none">
        <IonLabel className="ion-text-wrap">{titulo}</IonLabel>
      </IonItem>
      <IonList>
        {opciones.map((o) => (
          <IonItem key={o.codigo}>
            <IonLabel className="ion-text-wrap">{o.etiqueta}</IonLabel>
            <IonCheckbox
              slot="start"
              checked={seleccionados.includes(o.codigo)}
              onIonChange={() => setSeleccionados(toggleMulti(seleccionados, o.codigo))}
            />
          </IonItem>
        ))}
      </IonList>
    </>
  );

  const titulosPaso = [
    "Sección 1 — Identificación del Repro",
    "Sección 2 — Infraestructura de Residuos",
    seccion3 === "A"
      ? "Sección 3A — Estimación: Residuos Generales"
      : seccion3 === "B"
      ? "Sección 3B — Estimación: Residuos Diferenciados"
      : "Sección 3 — Estimación de Volumen",
    "Sección 4 — Observaciones Finales",
  ];

  const renderPaso1 = () => (
    <IonCard>
      <IonCardHeader>
        <IonCardTitle>{titulosPaso[0]}</IonCardTitle>
      </IonCardHeader>
      <IonCardContent>
        <IonItem>
          <IonLabel position="stacked" className="ion-text-wrap">
            Punto de Servicio (solo asignados) *
          </IonLabel>
          <IonInput
            placeholder={esEdicion ? psSeleccionado?.ps_nombre || "" : "Buscar..."}
            value={psBusqueda}
            disabled={esEdicion}
            onIonChange={(e) => setPsBusqueda(String(e.detail.value || ""))}
          />
        </IonItem>
        {psBuscando && <IonText color="medium">Buscando…</IonText>}
        {!esEdicion && psResultados.length > 0 && !psSeleccionado && (
          <IonList>
            {psResultados.map((p) => (
              <IonItem key={p.id} button onClick={() => seleccionarPS(p)}>
                <IonLabel>
                  <h3>{p.ps_nombre}</h3>
                  <p>{p.ps_codigo}</p>
                </IonLabel>
              </IonItem>
            ))}
          </IonList>
        )}
        {psSeleccionado && (
          <IonText color="success">
            <p>
              Seleccionado: <strong>{psSeleccionado.ps_nombre}</strong> ({psSeleccionado.ps_codigo})
            </p>
          </IonText>
        )}

        <IonItem lines="full">
          <IonLabel position="stacked">Fiscal asignado</IonLabel>
          <IonText color={fiscalSeleccionado ? "dark" : "medium"}>
            <p style={{ margin: "8px 0" }}>
              {fiscalSeleccionado?.fiscal_nombre ||
                "(se completa automáticamente al seleccionar el punto de servicio)"}
            </p>
          </IonText>
        </IonItem>

        <IonItem lines="full">
          <IonLabel position="stacked">Fecha del Repro</IonLabel>
          <IonText color="dark">
            <p style={{ margin: "8px 0" }}>{fechaRelevamiento}</p>
          </IonText>
        </IonItem>

        <IonItem lines="full">
          <IonLabel position="stacked" className="ion-text-wrap">
            Segmento Operativo
          </IonLabel>
          <IonText color={segmentoOperativo ? "dark" : "medium"}>
            <p style={{ margin: "8px 0" }}>
              {segmentoOperativo ||
                "(se completa automáticamente al seleccionar el punto de servicio)"}
            </p>
          </IonText>
        </IonItem>

        {renderSelect(
          "Cantidad aproximada de personas (externas) que trabajan o circulan por día *",
          cantidadPersonas,
          setCantidadPersonas,
          catalogo.cantidad_personas
        )}
        {cantidadPersonas === "OTRO" && (
          <IonItem>
            <IonLabel position="stacked">Especificar</IonLabel>
            <IonInput
              value={cantidadPersonasOtro}
              onIonChange={(e) => setCantidadPersonasOtro(String(e.detail.value || ""))}
            />
          </IonItem>
        )}
      </IonCardContent>
    </IonCard>
  );

  const renderPaso2 = () => (
    <IonCard>
      <IonCardHeader>
        <IonCardTitle>{titulosPaso[1]}</IonCardTitle>
      </IonCardHeader>
      <IonCardContent>
        {renderSelect(
          "¿El cliente tiene un espacio físico destinado a residuos? *",
          tieneEspacio,
          setTieneEspacio,
          catalogo.tiene_espacio_residuos
        )}
        {renderSelect(
          "¿El cliente pesa sus residuos? *",
          pesa,
          setPesa,
          catalogo.pesa_residuos
        )}
        {pesa === "OTRO" && (
          <IonItem>
            <IonLabel position="stacked">Detallar</IonLabel>
            <IonInput value={pesaOtro} onIonChange={(e) => setPesaOtro(String(e.detail.value || ""))} />
          </IonItem>
        )}
        {renderSelect(
          "¿El cliente tiene algún reporte o registro escrito de los residuos? *",
          reporte,
          setReporte,
          catalogo.reporte_residuos
        )}
        {reporte === "OTRO" && (
          <IonItem>
            <IonLabel position="stacked">Detallar</IonLabel>
            <IonInput
              value={reporteOtro}
              onIonChange={(e) => setReporteOtro(String(e.detail.value || ""))}
            />
          </IonItem>
        )}
        {renderCheckboxGroup(
          "¿Qué tipos de residuos genera este cliente? * (seleccione todos los que apliquen)",
          tiposResiduos,
          setTiposResiduos,
          catalogo.tipos_residuos
        )}
        {renderSelect(
          "De los materiales RECICLABLES, ¿cuál es el que más se genera? *",
          reciclableMas,
          setReciclableMas,
          catalogo.reciclable_mas_generado
        )}
        {renderSelect(
          "¿Tiene basureros o contenedores diferenciados por tipo de residuo? *",
          contenedores,
          setContenedores,
          catalogo.contenedores_diferenciados
        )}
        {contenedores && (
          <IonText color="primary">
            <p>
              → Próximo paso: <strong>Sección 3{seccion3}</strong>
            </p>
          </IonText>
        )}
      </IonCardContent>
    </IonCard>
  );

  const renderPaso3 = () => (
    <IonCard>
      <IonCardHeader>
        <IonCardTitle>{titulosPaso[2]}</IonCardTitle>
      </IonCardHeader>
      <IonCardContent>
        {seccion3 === "A" && (
          <>
            {renderSelect(
              "En un día normal, ¿cuántas bolsas de residuos se retiran?",
              bolsasGenerales,
              setBolsasGenerales,
              catalogo.bolsas
            )}
            {bolsasGenerales === "OTRO" && (
              <IonItem>
                <IonLabel position="stacked" className="ion-text-wrap">
                  Especificar cantidad de bolsas
                </IonLabel>
                <IonInput
                  value={bolsasGeneralesOtro}
                  placeholder="Ej.: media bolsa, 12 bolsas, varía mucho…"
                  onIonChange={(e) => setBolsasGeneralesOtro(String(e.detail.value || ""))}
                />
              </IonItem>
            )}
            {renderSelect(
              "¿Cuántos días por semana se retiran los residuos?",
              diasGenerales,
              setDiasGenerales,
              catalogo.dias_semana
            )}
            {renderCheckboxGroup(
              "¿Quién retira actualmente los residuos de este cliente?",
              quienRetiraGeneral,
              setQuienRetiraGeneral,
              catalogo.quien_retira
            )}
          </>
        )}
        {seccion3 === "B" && (
          <>
            {renderSelect(
              "En un día normal, ¿cuántas bolsas de residuos orgánicos se retiran?",
              bolsasOrganicas,
              setBolsasOrganicas,
              catalogo.bolsas
            )}
            {bolsasOrganicas === "OTRO" && (
              <IonItem>
                <IonLabel position="stacked" className="ion-text-wrap">
                  Especificar cantidad de bolsas orgánicas
                </IonLabel>
                <IonInput
                  value={bolsasOrganicasOtro}
                  placeholder="Ej.: media bolsa, 12 bolsas, varía mucho…"
                  onIonChange={(e) => setBolsasOrganicasOtro(String(e.detail.value || ""))}
                />
              </IonItem>
            )}
            {renderSelect(
              "¿Cuántos días por semana se retiran residuos orgánicos?",
              diasOrganicos,
              setDiasOrganicos,
              catalogo.dias_semana
            )}
            {renderSelect(
              "En un día normal, ¿cuántas bolsas de materiales reciclables se juntan?",
              bolsasReciclables,
              setBolsasReciclables,
              catalogo.bolsas
            )}
            {bolsasReciclables === "OTRO" && (
              <IonItem>
                <IonLabel position="stacked" className="ion-text-wrap">
                  Especificar cantidad de bolsas reciclables
                </IonLabel>
                <IonInput
                  value={bolsasReciclablesOtro}
                  placeholder="Ej.: media bolsa, 12 bolsas, varía mucho…"
                  onIonChange={(e) => setBolsasReciclablesOtro(String(e.detail.value || ""))}
                />
              </IonItem>
            )}
            {renderSelect(
              "¿Los reciclables van mezclados o separados por tipo?",
              reciclablesMezclados,
              setReciclablesMezclados,
              catalogo.reciclables_mezclados
            )}
            {renderSelect(
              "¿Cuántos días por semana se juntan reciclables suficientes para llenar al menos una bolsa?",
              diasReciclables,
              setDiasReciclables,
              catalogo.dias_reciclables
            )}
            {renderCheckboxGroup(
              "¿Quién retira actualmente los residuos de este cliente?",
              quienRetira3b,
              setQuienRetira3b,
              catalogo.quien_retira
            )}
            <IonItem>
              <IonLabel position="stacked" className="ion-text-wrap">
                ¿Sabés el nombre de quien retira los reciclables? En su defecto, ¿en qué tipo de vehículo? (camión, camioneta, motocarro…)
              </IonLabel>
              <IonInput
                value={retiraReciclablesInfo}
                placeholder="Ej.: camión, camioneta, motocarro, nombre de empresa…"
                onIonChange={(e) => setRetiraReciclablesInfo(String(e.detail.value || ""))}
              />
            </IonItem>
            {renderSelect(
              "¿Con qué frecuencia retiran los reciclables?",
              frecuenciaReciclables,
              setFrecuenciaReciclables,
              catalogo.frecuencia_retiran_reciclables
            )}
          </>
        )}
        {seccion3 === "" && (
          <IonText color="medium">
            Volvé a la Sección 2 y elegí si el cliente tiene contenedores diferenciados.
          </IonText>
        )}
      </IonCardContent>
    </IonCard>
  );

  const renderPaso4 = () => (
    <>
      <IonCard>
        <IonCardHeader>
          <IonCardTitle>{titulosPaso[3]}</IonCardTitle>
        </IonCardHeader>
        <IonCardContent>
          <IonItem>
            <IonLabel position="stacked" className="ion-text-wrap">
              ¿Hay algún material relevante que no esté cubierto por las preguntas anteriores?
            </IonLabel>
            <IonTextarea
              rows={3}
              value={materialNoCubierto}
              placeholder="Materiales inusuales, volúmenes muy altos o muy bajos, situaciones especiales"
              onIonChange={(e) => setMaterialNoCubierto(String(e.detail.value || ""))}
            />
          </IonItem>
          <IonItem>
            <IonLabel position="stacked" className="ion-text-wrap">
              ¿Hay algo del contexto de este cliente que sea importante entender?
            </IonLabel>
            <IonTextarea
              rows={3}
              value={contextoCliente}
              placeholder="Obras en curso, cambios recientes, materiales especiales, problemas particulares"
              onIonChange={(e) => setContextoCliente(String(e.detail.value || ""))}
            />
          </IonItem>

          <IonItem lines="none">
            <IonLabel className="ion-text-wrap">Fotos adicionales</IonLabel>
          </IonItem>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={onSeleccionarFotos}
          />
          <input
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: "none" }}
            id="rel-op-camera-input"
            onChange={onSeleccionarFotos}
          />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
            <IonButton
              size="small"
              onClick={() => document.getElementById("rel-op-camera-input")?.click()}
            >
              <IonIcon slot="start" icon={cameraOutline} />
              Tomar foto
            </IonButton>
            <IonButton
              size="small"
              fill="outline"
              onClick={() => fileInputRef.current?.click()}
            >
              <IonIcon slot="start" icon={imageOutline} />
              Elegir de galería
            </IonButton>
          </div>
          {fotos.length > 0 && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                gap: 8,
                marginTop: 12,
              }}
            >
              {fotos.map((f, idx) => (
                <div
                  key={idx}
                  style={{ position: "relative", border: "1px solid #ddd", borderRadius: 4 }}
                >
                  <img
                    src={f.url_secure || f.data_url}
                    alt={f.nombre_archivo || `foto-${idx + 1}`}
                    style={{ width: "100%", height: 120, objectFit: "cover", display: "block" }}
                  />
                  <IonButton
                    size="small"
                    color="danger"
                    style={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      "--padding-start": "4px",
                      "--padding-end": "4px",
                      "--padding-top": "4px",
                      "--padding-bottom": "4px",
                    } as React.CSSProperties}
                    onClick={() => eliminarFoto(idx)}
                  >
                    <IonIcon icon={trashOutline} />
                  </IonButton>
                </div>
              ))}
            </div>
          )}
        </IonCardContent>
      </IonCard>

      {esEdicion && (
        <IonCard>
          <IonCardHeader>
            <IonCardTitle>Motivo de la edición (opcional)</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonItem>
              <IonTextarea
                rows={2}
                value={motivoEdicion}
                placeholder="Por ej.: corregí un dato, agregué fotos, etc."
                onIonChange={(e) => setMotivoEdicion(String(e.detail.value || ""))}
              />
            </IonItem>
          </IonCardContent>
        </IonCard>
      )}
    </>
  );

  // IMPORTANTE: renderizamos los 4 pasos SIEMPRE y ocultamos con display:none los
  // que no son el actual. Si desmontamos un paso, los IonSelect del paso disparan
  // ionChange con valor vacío al desmontarse, lo que pisa el state con "".
  const renderPasoActual = () => (
    <>
      <div style={{ display: paso === 1 ? "block" : "none" }}>{renderPaso1()}</div>
      <div style={{ display: paso === 2 ? "block" : "none" }}>{renderPaso2()}</div>
      <div style={{ display: paso === 3 ? "block" : "none" }}>{renderPaso3()}</div>
      <div style={{ display: paso === 4 ? "block" : "none" }}>{renderPaso4()}</div>
    </>
  );

  return (
    <IonPage>
      <Header titulo={esEdicion ? "Editar Repro" : "Repro"} />
      <IonContent id="relevamiento-operativo" className="ion-padding">
        {/* Encabezado del wizard */}
        <div style={{ marginBottom: 12 }}>
          <IonText>
            <h3 style={{ margin: "4px 0" }}>
              Paso {paso} de {totalPasos}
            </h3>
            <p style={{ margin: "0 0 8px 0", color: "#666" }}>{titulosPaso[paso - 1]}</p>
          </IonText>
          <IonProgressBar value={paso / totalPasos} />
        </div>

        {renderPasoActual()}

        {/* Navegación */}
        <div style={{ display: "flex", gap: 8, marginTop: 16, marginBottom: 32, flexWrap: "wrap" }}>
          {paso > 1 && (
            <IonButton
              fill="outline"
              disabled={guardando}
              onClick={irAnterior}
              style={{ flex: 1, minWidth: 120 }}
            >
              <IonIcon slot="start" icon={arrowBackOutline} />
              Anterior
            </IonButton>
          )}
          {paso < totalPasos && (
            <IonButton
              disabled={guardando}
              onClick={irSiguiente}
              style={{ flex: 1, minWidth: 120 }}
            >
              Siguiente
              <IonIcon slot="end" icon={arrowForwardOutline} />
            </IonButton>
          )}
          {paso === totalPasos && (
            <IonButton
              color="success"
              disabled={guardando}
              onClick={guardar}
              style={{ flex: 1, minWidth: 120 }}
            >
              {guardando ? (
                <IonSpinner name="dots" />
              ) : (
                <>
                  <IonIcon slot="start" icon={saveOutline} />
                  {esEdicion ? "Guardar cambios" : "Guardar"}
                </>
              )}
            </IonButton>
          )}
          <IonButton
            color="medium"
            fill="clear"
            disabled={guardando}
            onClick={() => history.replace("/relevamiento-operativo")}
            style={{ flexBasis: "100%" }}
          >
            Cancelar
          </IonButton>
        </div>

        <IonToast
          isOpen={toast.show}
          message={toast.msg}
          color={toast.color}
          duration={3500}
          onDidDismiss={() => setToast({ show: false, msg: "" })}
        />
      </IonContent>
    </IonPage>
  );
};

export default RelevamientoOperativo;
